import { useTranslation } from 'contexts/Localization'
import { useCallback, useEffect, useState } from 'react'
import { useDebounce, usePrevious } from 'react-use'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { Rarity } from 'state/vvsgotchi/models'
import { Link, Text, Flex, ChevronDownIcon, ChevronUpIcon } from 'vvs-uikit'
import { AddMoleButton } from '../styles'
import { LeaderboardFooterRow } from './LeaderboardRow'
import { ExpandButton, ExpandButtonWrapper, InnerScrollWrapper, FooterContainer, ToTopButton } from './styles'

interface IFooterProps {
  rarity: Rarity
  isTopIntersecting: boolean
  scrollToTop?: () => void
}
const LeaderboardFooter: React.FC<IFooterProps> = ({ rarity, isTopIntersecting, scrollToTop }) => {
  const { moles } = useGotchiOwner()
  const filteredMoles = moles?.filter((m) => m.rarity === rarity)
  const prevRarity = usePrevious(rarity)

  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [displayData, setDisplayData] = useState([])

  const isEmpty = !filteredMoles || filteredMoles.length === 0

  useDebounce(
    () => {
      if (!isExpanded && !isEmpty) setDisplayData([filteredMoles[0]])
    },
    300,
    [isExpanded, isEmpty],
  )

  useEffect(() => {
    if (prevRarity !== rarity) {
      setDisplayData(filteredMoles)
      if (isExpanded) setIsExpanded(false)
    }
  }, [prevRarity, rarity, filteredMoles, isExpanded])

  const handleClick = useCallback(() => {
    const changing = !isExpanded
    if (changing && filteredMoles) {
      setDisplayData(filteredMoles)
    }
    setIsExpanded(changing)
  }, [isExpanded, filteredMoles])

  return (
    <FooterContainer expanded={isExpanded} empty={isEmpty}>
      <ExpandButtonWrapper>
        {!isEmpty && filteredMoles.length > 1 && (
          <ExpandButton>
            {isExpanded ? (
              <>
                <ChevronDownIcon fill="white" width="16px" />
                <Text fontSize="11px" color="white" onClick={handleClick}>
                  {t('Show Less')}
                </Text>
              </>
            ) : (
              <>
                <ChevronUpIcon fill="white" width="16px" />
                <Text fontSize="11px" color="white" onClick={handleClick}>
                  {filteredMoles.length - 1 === 1
                    ? t('1 more mole')
                    : t('%numOfItem% more moles', { numOfItem: filteredMoles.length - 1 })}
                </Text>
              </>
            )}
          </ExpandButton>
        )}
        {!isTopIntersecting && (
          <ToTopButton onClick={scrollToTop}>
            <ChevronUpIcon fill="white" />
          </ToTopButton>
        )}
      </ExpandButtonWrapper>
      {isEmpty ? (
        <Flex gap="12px" alignItems="center" px="15px" py="12px">
          <AddMoleButton
            isDark
            as={Link}
            href="https://minted.network/collections/cronos/0x06596ed89ac4609de47a21af7e36b38b2df57c26"
            target="_blank"
          >
            <Text bold color="success" fontSize="28px">
              +
            </Text>
          </AddMoleButton>
          <Text bold color="white">
            {t('Adopt a Miner Mole here!')}
          </Text>
        </Flex>
      ) : (
        <InnerScrollWrapper>
          {displayData.map((m, i) => (
            <LeaderboardFooterRow key={m.tokenId} mole={m} index={i} />
          ))}
        </InnerScrollWrapper>
      )}
    </FooterContainer>
  )
}
export default LeaderboardFooter
